import { useMemo } from 'react';
import type { Hoja } from '@/api/laminas';

// La pirámide de tiles, sacada del visor para poder pintar DOS hojas a la vez
// (F-6). Era código único dentro de `VisorLamina` y mientras hubo una sola hoja
// eso estuvo bien; comparar dos versiones lo pedía duplicado, que es la señal de
// que tenía que salir.
//
// El mecanismo es el de Fieldwire: tiles JPEG pre-renderizadas y un `<img>` por
// tile VISIBLE. El navegador no abre el PDF nunca. La pirámide es corta — z0
// (detalle) y z2 (vista completa) — y el nivel intermedio lo escala el navegador.

export interface Vista {
  escala: number;
  x: number;
  y: number;
}

export interface Tile {
  x: number;
  y: number;
  w: number;
  h: number;
  url: string;
}

/**
 * Qué tiles hacen falta para pintar `hoja` con esta `vista` dentro de un
 * contenedor de `tam`. Devuelve el nivel elegido y solo las tiles que
 * intersectan el viewport.
 *
 * La `vista` que se le pasa es la de la hoja: para la hoja comparada incluye su
 * propio desfase y su propia escala, así que el recorte del viewport sale
 * correcto sin que el hook sepa nada de la comparación.
 */
export function useTiles(hoja: Hoja | null | undefined, vista: Vista, tam: { w: number; h: number }) {
  // Niveles disponibles, de más detalle a menos. Si la hoja no declara nada se
  // asume solo el 0: una tile por pedazo a tamaño natural.
  const niveles = useMemo(() => {
    const crudos = (hoja?.nivelesTiles || '0')
      .split(',').map(s => Number.parseInt(s.trim(), 10))
      .filter(n => Number.isFinite(n) && n >= 0);
    return crudos.length ? [...new Set(crudos)].sort((a, b) => a - b) : [0];
  }, [hoja?.nivelesTiles]);

  // El nivel elegido: queremos que 2^z ≈ 1/escala (un píxel de tile ≈ un píxel
  // de pantalla). Se toma el nivel MÁS GRANDE que no se vea borroso, y se cae
  // al más detallado disponible cuando se está muy cerca.
  const nivel = useMemo(() => {
    const ideal = Math.log2(1 / Math.max(vista.escala, 1e-6));
    let elegido = niveles[0];
    for (const n of niveles) if (n <= ideal) elegido = n;
    return elegido;
  }, [vista.escala, niveles]);

  const visibles = useMemo<Tile[]>(() => {
    if (!hoja?.baseUrlTiles || !tam.w) return [];
    // Cuántos píxeles de la hoja cubre una tile de este nivel.
    const paso = (hoja.tileSize || 1024) * 2 ** nivel;
    const cols = Math.ceil(hoja.anchoPx / paso);
    const filas = Math.ceil(hoja.altoPx / paso);

    // Con un anillo de 1 de margen para que al arrastrar no se vea el borde
    // en blanco.
    const x0 = Math.max(0, Math.floor((-vista.x / vista.escala) / paso) - 1);
    const y0 = Math.max(0, Math.floor((-vista.y / vista.escala) / paso) - 1);
    const x1 = Math.min(cols - 1, Math.floor(((tam.w - vista.x) / vista.escala) / paso) + 1);
    const y1 = Math.min(filas - 1, Math.floor(((tam.h - vista.y) / vista.escala) / paso) + 1);

    const salida: Tile[] = [];
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        salida.push({
          x: x * paso,
          y: y * paso,
          // Las tiles del borde cubren menos hoja que las del centro. Darles el
          // `paso` completo estiraría la imagen parcial.
          w: Math.min(paso, hoja.anchoPx - x * paso),
          h: Math.min(paso, hoja.altoPx - y * paso),
          // Convención del ingestor: `BaseUrlTiles` es el prefijo y se le pega
          // `_{z}_{x}_{y}.jpg`. Es la misma forma que usa Fieldwire
          // (`{hash}_V1_{z}_{x}_{y}.jpeg`), con el prefijo ya resuelto en la base.
          // `tokenTiles` es la firma de lectura del blob y va al final, después
          // del nombre: en producción las tiles salen de un contenedor privado.
          url: `${hoja.baseUrlTiles}_${nivel}_${x}_${y}.jpg${hoja.tokenTiles || ''}`,
        });
      }
    }
    return salida;
  }, [hoja?.baseUrlTiles, hoja?.tokenTiles, hoja?.anchoPx, hoja?.altoPx, hoja?.tileSize,
      tam.w, tam.h, vista.x, vista.y, vista.escala, nivel]);

  return { nivel, visibles };
}

/** El papel. Nada más: ni marcas, ni chinches, ni gestos. */
export function CapaTiles({ hoja, nivel, visibles }: {
  hoja: Hoja;
  nivel: number;
  visibles: Tile[];
}) {
  if (hoja.baseUrlTiles) {
    return (
      <>
        {visibles.map(t => (
          <img
            key={`${nivel}-${t.x}-${t.y}`}
            src={t.url}
            alt=""
            draggable={false}
            className="absolute select-none"
            style={{ left: t.x, top: t.y, width: t.w, height: t.h }}
          />
        ))}
      </>
    );
  }
  if (hoja.urlOriginal) {
    // Sin teselar: se muestra la imagen entera. Sirve para probar el visor
    // antes de que exista el ingestor, pero no escala — una lámina de 11.000 px
    // así pesa decenas de MB.
    return (
      <img src={hoja.urlOriginal} alt="" draggable={false}
        className="absolute left-0 top-0 select-none"
        style={{ width: hoja.anchoPx, height: hoja.altoPx }} />
    );
  }
  return (
    <div className="flex h-full w-full items-center justify-center p-8 text-center text-sm text-brand-gray-400">
      Esta hoja no tiene ni tiles ni imagen original: falta pasarla por el ingestor.
    </div>
  );
}
